import { useMutation, useQuery } from 'react-query';
import { HiChevronRight } from 'react-icons/hi2';
import { useContext, useEffect, useState } from 'react';
import { Col, Row } from 'antd';
import { useHistory } from 'react-router-dom';
import { Box, Button } from 'modules/v2/common/AtomicDesign/atoms';
import RelativeLoading from 'modules/v2/common/AtomicDesign/RelativeLoading';
import { AddCardModal, ProcessingModal } from 'modules/v2/common/AtomicDesign/organisms';
import ConfirmationModal from 'modules/v2/common/AtomicDesign/organisms/ConfirmationModal';
import { getDefaultPaymentProfile, getPaymentProfilesList, sendPaymentResolved } from 'modules/api';
import { ROUTE_BOOK_CATALOG } from 'modules/v2/routes/constants';
import ObjectSet from 'modules/v2/common/utils/set';
import { PageMapping } from '../../index';
import { BuyCreditsContext } from '../../context';
import { toUSD } from '../../helper/stringHelper';
import { customLoadingModalTheme } from './theme';

type Props = {
  setActivePage: (value: PageMapping) => void;
};

const BuyCreditsCheckoutPage: React.FC<Props> = ({ setActivePage }) => {
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showPaymentLoadingModal, setShowPaymentLoadingModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [availablePaymentOptions, setAvailablePaymentOptions] = useState(new ObjectSet());

  const [defaultPaymentOption, setDefaultPaymentOption] = useState(null);

  const history = useHistory();

  const { userCredits, selectedCreditsOffer } = useContext(BuyCreditsContext);

  const {
    data: defaultProfile,
    isLoading: defaultProfileLoading,
    isRefetching: defaultProfileRefetching,
  } = useQuery('getDefaultPaymentProfile', getDefaultPaymentProfile, {
    onSuccess: (data) => {
      setDefaultPaymentOption(data);
    },
    onError: (error) => {
      console.error('Error fetching default payment profile:', error);
    },
  });

  const {
    data: paymentData = [],
    isLoading: loadingGetPaymentProfilesList,
    isRefetching: refetchingGetPaymentProfilesList,
  } = useQuery('getPaymentProfilesList', getPaymentProfilesList, {
    onSuccess: (data) => {
      const set = new ObjectSet();
      data.forEach((item) => set.add(item));
      setAvailablePaymentOptions(set);
    },
  });

  const navigateBack = () => {
    setActivePage({
      id: 'buy-credits',
      title: 'Buy credits',
    });
  };

  useEffect(() => {
    if (defaultPaymentOption) {
      const currentSet = availablePaymentOptions;
      currentSet.add(defaultPaymentOption);
      setAvailablePaymentOptions(currentSet);
      setSelectedPayment(defaultPaymentOption);
    }
  }, [defaultPaymentOption]);

  const { mutate: mutateSendPayment, isLoading: isPaymentLoading } = useMutation(
    'sendPaymentResolved',
    sendPaymentResolved,
    {
      onSuccess: async () => {
        setShowPaymentLoadingModal(false);
        setShowConfirmationModal(true);
      },
      onError: (error: { response: { data: { errors: string[] } }; data: { message: string } }) => {
        setShowPaymentLoadingModal(false);
        console.error(error.response.data.errors?.[0] || error?.data?.message || 'Network Error');
      },
    },
  );

  const handlePayment = () => {
    setShowPaymentLoadingModal(true);
    const chargeItemPriceId = selectedCreditsOffer?.product.chargebeeComponentId;
  };

  return (
    <>
      <div id="breadcrumbs" className="flex gap-2 items-center font-figtree">
        <span className="cursor-pointer hover:bg-neutral-100" onClick={navigateBack}>
          Credits
        </span>{' '}
        <HiChevronRight />
        <span className="border-b-[1px] border-neutral-300">Payment</span>
      </div>

      <Box className="mt-6 relative font-figtree">
        <RelativeLoading isLoading={showNotification} className="rounded-lg" />

        <div className="flex flex-col border-b-[1px] p-[22px] ps-6 pe-6">
          <span className="text-base font-semibold">Review and pay</span>
        </div>

        <Row className="p-6 justify-center max-[1250px]:flex-col" gutter={[24, 24]}>
          <div className="min-w-80">
            <div
              id="summary"
              className="justify-items-center bg-neutral-50 rounded-lg p-7 min-w-80"
            >
              <span className="text-2xl font-semibold">Summary</span>

              <Row gutter={6} className="mt-7 mb-7 w-4/5">
                <Col span={12}>
                  <span className="text-base">{selectedCreditsOffer.credits} credits</span>
                </Col>

                <Col
                  span={12}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    width: '100%',
                  }}
                >
                  <span className="text-base">{toUSD(selectedCreditsOffer.totalAmount)}</span>
                </Col>
              </Row>

              <hr />

              <Row gutter={6} className="mt-7 w-4/5">
                <Col span={12}>
                  <span className="text-lg font-semibold">Total due today</span>
                </Col>

                <Col
                  span={12}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    width: '100%',
                  }}
                >
                  <span className="text-lg font-semibold">
                    {toUSD(selectedCreditsOffer.totalAmount)}
                  </span>
                </Col>
              </Row>

              <Row className="mt-7">
                <Button onClick={handlePayment} wrapperClassName="w-full" size="lg">
                  Pay now
                </Button>
              </Row>

              <Row>
                <span className="text-sm text-neutral-500 mt-4">
                  By clicking “Pay now”, I agree to the{' '}
                  <span className="w-full underline underline-offset-2 text-primary-500 hover:bg-primary-50 rounded transition duration-300 ease-in-out px-[2px] -mx-[2px] hover:p-[4px] hover:-mx-[4px]">
                    <a
                      className="underline underline-offset-2 text-primary-500"
                      href="https://authorify.com/terms-of-use/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      terms and conditions.
                    </a>
                  </span>
                </span>
              </Row>
            </div>
          </div>
        </Row>
      </Box>

      {showAddCardModal && (
        <AddCardModal showModal={showAddCardModal} setShowModal={setShowAddCardModal} />
      )}

      <ProcessingModal
        title="Processing your payment..."
        description="Please don’t close this window"
        showModal={showPaymentLoadingModal || isPaymentLoading || isCheckoutLoading}
        setShowModal={setShowPaymentLoadingModal}
        theme={customLoadingModalTheme}
      />

      <ConfirmationModal
        title={`You've successfully purchased ${selectedCreditsOffer.credits} credits!`}
        description={`You now have a total of ${parseInt(selectedCreditsOffer.credits, 10) +
          parseInt(
            userCredits,
            10,
          )} credits in your account. If its not reflected on your account yet, please wait at least 5 minutes.`}
        showModal={showConfirmationModal}
        setShowModal={setShowConfirmationModal}
        onConfirmation={() => {
          history.push(ROUTE_BOOK_CATALOG);
        }}
      />
    </>
  );
};

export default BuyCreditsCheckoutPage;
